// The booking wizard's wording in Japanese; keys match en.ts. Machine-drafted, flagged for native review.
import type { en } from "./en";

export const ja = {
  // Page
  schedule_appointment: "予約する",
  powered_by: "提供",

  // Patient type
  new_or_existing: "初診の方ですか、再診の方ですか？",
  new_patient: "初診",
  existing_patient: "再診",
  first_time_visiting: "この医院を受診するのは初めてです",
  been_here_before: "以前に受診したことがあります",

  // Appointment info
  what_type: "どのようなご予約ですか？",
  no_types_available: "現在、オンラインで予約できる診療はありません。",
  minutes: "%{count}分",
  additional_info: "ご相談したい問題や気になることはありますか？",
  describe_concern: "気になる症状をご記入ください...",

  // Provider
  pick_provider: "担当医を選ぶ",
  loading_providers: "担当医を読み込み中...",
  failed_load_providers: "担当医を読み込めませんでした。もう一度お試しください。",
  any_provider: "指定なし",
  show_all_available: "空いている時間をすべて表示",
  no_availability: "空きなし",

  // Time
  pick_a_time: "時間を選ぶ",
  finding_times: "空いている時間を探しています...",
  no_times_for_date: "この日に空いている時間はありません",
  slot_just_taken:
    "申し訳ありません。その時間はたった今予約が入りました。別の時間をお選びください。",
  jump_to_next: "次に空いている時間へ移動",
  morning: "午前",
  afternoon: "午後",
  cant_find_time: "ご希望の時間が見つかりませんか？医院にお電話いただければお手伝いいたします。",

  // Patient info
  your_information: "患者情報",
  first_name: "名",
  last_name: "姓",
  preferred_name: "呼ばれたいお名前",
  optional: "任意",
  date_of_birth: "生年月日",
  phone_number: "電話番号",
  required: "必須",
  invalid_phone: "有効な10桁の電話番号を入力してください",
  min_age: "患者は5歳以上である必要があります",
  future_date: "未来の日付は入力できません",
  referral_question: "当院を何でお知りになりましたか？",
  referral_prompt: "選択してください",
  referral_detail_who: "どなたからのご紹介ですか？",
  referral_detail_other: "当院をお知りになったきっかけを教えてください",
  referral_detail_optional: "詳細（任意）",
  verification_disclaimer: "この番号に認証コードをお送りします。",
  phone_verified: "電話番号の認証が完了しました",
  verifying: "認証中...",
  verify: "認証する",
  invalid_code: "コードが正しくありません。もう一度お試しください。",
  sending: "送信中...",
  resend_code: "コードを再送信",
  email: "メールアドレス",
  send_verification: "認証コードを送信",
  sending_code: "コードを送信中...",

  // Insurance
  insurance_info: "保険情報",
  have_insurance: "保険に加入していますか？",
  insurance_changed: "前回のご来院から保険は変わりましたか？",
  no_insurance_change: "変更なし",
  yes: "はい",
  no: "いいえ",
  insurance_name: "保険会社名",
  subscriber_name: "被保険者の氏名",
  subscriber_dob: "被保険者の生年月日",
  subscriber_id: "被保険者番号",
  group_number: "グループ番号",
  select_option: "選択してください",
  card_photo_title: "保険証の写真",
  card_photo_hint: "ご来院前に補償内容を確認できるよう、保険証の両面の写真を追加してください。",
  card_front: "保険証の表面",
  card_back: "保険証の裏面",
  add_photo: "写真を追加",
  uploading_photo: "写真をアップロード中...",
  remove_photo: "削除",
  card_upload_failed:
    "写真を追加できませんでした。もう一度お試しいただくか、写真なしで続けてください。",
  card_try_again: "もう一度試す",

  // Review
  review_appointment: "予約内容の確認",
  appointment_details: "予約の詳細",
  type_label: "種類",
  provider: "担当医",
  any_available: "空いている担当医",
  date_and_time: "日時",
  notes: "メモ",
  patient_information: "患者情報",
  name: "氏名",
  phone: "電話番号",
  insurance: "保険",
  subscriber: "被保険者",
  subscriber_id_label: "被保険者番号",
  group_hash: "グループ番号",
  no_insurance: "保険なし",
  card_photos: "保険証の写真",
  card_front_short: "表面",
  card_back_short: "裏面",
  waitlist: "もっと早い時間が空いたら連絡してほしい",
  consent_prefix: "予約することで、",
  terms: "利用規約",
  consent_and: "と",
  privacy: "プライバシーポリシー",
  consent_suffix:
    "に同意したものとみなされます。電話番号をご提供いただくと、ショートメッセージの受信に同意したことになります。メッセージおよびデータ通信料がかかる場合があります。",
  scheduling: "予約中...",
  schedule_appointment_btn: "予約する",
  continue_to_payment: "お支払いへ進む",

  // Deposit
  deposit_title: "預かり金のお支払い",
  deposit_explainer:
    "%{amount}の預かり金をお支払いいただくと、このご予約が確定します。預かり金はアカウントに入金され、治療費に充てられます。ご予約に来られなかった場合、預かり金は返金されません。",
  deposit_due: "預かり金",
  deposit_card_fee: "カード手数料",
  deposit_card_fee_notice:
    "クレジットカードでのお支払いには%{percentage}%の手数料が含まれます。デビットカードには手数料はかかりません。",
  deposit_total: "合計",
  deposit_loading: "安全なお支払い画面を読み込み中...",
  deposit_paying: "処理中...",
  deposit_pay_and_book: "支払って予約する",
  deposit_card_error:
    "預かり金をお支払いいただけませんでした。カードをご確認のうえ、もう一度お試しください。",
  deposit_unavailable: "安全なお支払い画面を読み込めませんでした。医院にお電話ください。",
  deposit_needs_hold: "この時間を確保できませんでした。もう一度時間をお選びください。",
  deposit_processing:
    "お支払いを確認中です。再度お支払いにならないでください — まもなく医院がご予約を確定します。",

  // Confirmation
  appointment_requested: "ご予約のリクエストを受け付けました！",
  confirm_message:
    "医院で確認のうえ、確定のご連絡をお送りします。追加の情報が必要な場合は、スタッフからご連絡いたします。",
  date: "日付",
  time: "時間",
  add_to_google: "Googleカレンダーに追加",
  download_ics: "カレンダーファイルをダウンロード（.ics）",
  questions_call: "ご不明な点はお電話ください:",

  // Errors
  network_error: "問題が発生しました。もう一度お試しください。",

  // Common
  back: "戻る",
  continue: "続ける",
  review: "確認",

  // Step labels
  step_appointment: "予約",
  step_provider: "担当医",
  step_time: "時間",
  step_your_info: "患者情報",
  step_insurance: "保険",
} as const satisfies Record<keyof typeof en, string>;
